import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  ClipboardList,
  Eye,
  Mail,
  Package,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import { supabase } from "../../supabase-client";

const MONTHS_TO_SHOW = 12;

const monthFormatter = new Intl.DateTimeFormat("es-AR", {
  month: "short",
  year: "2-digit",
});

const numberFormatter = new Intl.NumberFormat("es-AR");
const dateTimeFormatter = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "short",
  timeStyle: "short",
});

const capitalize = (value) => {
  if (!value) return "Sin dato";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

const getReferrerLabel = (referrer) => {
  if (!referrer) return "Directo";

  try {
    const url = new URL(referrer);
    return url.hostname || "Directo";
  } catch {
    return String(referrer);
  }
};

const getDateValue = (item, fields) => {
  for (const field of fields) {
    if (item?.[field]) {
      const date = new Date(item[field]);
      if (!Number.isNaN(date.getTime())) return date;
    }
  }
  return null;
};

const buildMonthRange = (total = MONTHS_TO_SHOW) => {
  const output = [];
  const base = new Date();
  base.setDate(1);
  base.setHours(0, 0, 0, 0);

  for (let i = total - 1; i >= 0; i -= 1) {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    output.push({
      key,
      label: capitalize(monthFormatter.format(d)),
    });
  }

  return output;
};

const monthFromDate = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const makeMonthlySeries = (rows, dateFields, monthRange) => {
  const counter = monthRange.reduce((acc, item) => {
    acc[item.key] = 0;
    return acc;
  }, {});

  for (const row of rows || []) {
    const date = getDateValue(row, dateFields);
    const key = monthFromDate(date);
    if (key && counter[key] !== undefined) {
      counter[key] += 1;
    }
  }

  return monthRange.map((item) => ({
    key: item.key,
    month: item.label,
    value: counter[item.key] || 0,
  }));
};

const parseObraIds = (value) => {
  if (!value) return [];

  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((entry) => Number(entry?.id))
    .filter((id) => Number.isFinite(id));
};

const buildPalette = (total) => {
  const palette = [
    "#28536b",
    "#c2945a",
    "#5b8e7d",
    "#9b3d3d",
    "#9e7bb5",
    "#334e68",
    "#2f7d7f",
    "#b56576",
  ];

  return Array.from({ length: total }, (_, index) => palette[index % palette.length]);
};

function KpiCard({ title, value, subtitle, icon: Icon, tone = "blue" }) {
  const tones = {
    blue: "from-[#e7f0f6] to-white text-[#1f4a63]",
    amber: "from-[#fbf2e7] to-white text-[#7a4f1f]",
    green: "from-[#e9f5f0] to-white text-[#1f5a45]",
    red: "from-[#fbeaea] to-white text-[#7a2323]",
  };

  return (
    <article
      className={`rounded-2xl border border-stone-200 bg-gradient-to-br p-5 shadow-sm ${tones[tone] || tones.blue}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[0.7rem] uppercase tracking-[0.16em] opacity-70">{title}</p>
          <p className="mt-2 text-3xl font-black leading-none">{numberFormatter.format(value || 0)}</p>
        </div>
        <div className="rounded-xl bg-white/80 p-2 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="mt-3 text-xs opacity-80">{subtitle}</p>
    </article>
  );
}

function MiniBarChart({ title, description, series, color = "#28536b" }) {
  const max = Math.max(...series.map((item) => item.value), 1);

  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="mb-4">
        <h3 className="text-base font-bold text-stone-900">{title}</h3>
        <p className="text-xs text-stone-500">{description}</p>
      </div>

      <div className="h-60">
        <div className="flex h-full items-end gap-2">
          {series.map((item) => {
            const height = `${Math.max((item.value / max) * 100, item.value > 0 ? 6 : 2)}%`;
            return (
              <div key={item.key} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end">
                <div className="mb-2 text-[11px] font-semibold text-stone-600">{item.value}</div>
                <div className="w-full rounded-t-md transition-all duration-500" style={{ height, backgroundColor: color }} />
                <div className="mt-2 text-[10px] uppercase tracking-[0.08em] text-stone-500">{item.month}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DonutChart({ title, subtitle, data }) {
  const total = data.reduce((acc, item) => acc + item.value, 0);
  const radius = 70;
  const stroke = 24;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  let running = 0;

  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="mb-4">
        <h3 className="text-base font-bold text-stone-900">{title}</h3>
        <p className="text-xs text-stone-500">{subtitle}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-[180px,1fr] md:items-center">
        <div className="relative mx-auto h-[180px] w-[180px]">
          <svg height="180" width="180" viewBox="0 0 180 180" className="-rotate-90">
            {data.map((item) => {
              const ratio = total > 0 ? item.value / total : 0;
              const segment = circumference * ratio;
              const dashOffset = circumference - running;
              running += segment;

              return (
                <circle
                  key={item.name}
                  stroke={item.color}
                  fill="transparent"
                  strokeWidth={stroke}
                  strokeDasharray={`${segment} ${circumference}`}
                  strokeDashoffset={dashOffset}
                  r={normalizedRadius}
                  cx="90"
                  cy="90"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-3xl font-black text-stone-900">{numberFormatter.format(total)}</div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-stone-500">Obras</div>
          </div>
        </div>

        <div className="space-y-2">
          {data.map((item) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0;
            return (
              <div key={item.name} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-stone-700">
                  <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span>{item.name}</span>
                </div>
                <div className="text-sm font-bold text-stone-900">
                  {numberFormatter.format(item.value)} ({pct.toFixed(0)}%)
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Estadistica() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);

  const [kpis, setKpis] = useState({
    visitasTotal: 0,
    visitasUnicasTotal: 0,
    visitasPorReferrer: [],
    contactosTotal: 0,
    pedidosTotal: 0,
    obrasTotal: 0,
    consultasObraTotal: 0,
  });

  const [monthlyBars, setMonthlyBars] = useState([]);
  const [obraStatusData, setObraStatusData] = useState([]);
  const [obrasConsultadasPorMes, setObrasConsultadasPorMes] = useState([]);
  const [recentVisits, setRecentVisits] = useState([]);
  const [recentObraConsultas, setRecentObraConsultas] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");

  const monthRange = useMemo(() => buildMonthRange(MONTHS_TO_SHOW), []);

  const selectedMonthLabel = useMemo(() => {
    const found = monthRange.find((item) => item.key === selectedMonth);
    return found?.label || "Mes";
  }, [monthRange, selectedMonth]);

  const topConsultedThisMonth = useMemo(() => {
    return obrasConsultadasPorMes.find((item) => item.monthKey === selectedMonth)?.items || [];
  }, [obrasConsultadasPorMes, selectedMonth]);

  const loadDashboard = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    if (quiet) setRefreshing(true);
    setError("");

    try {
      const warningsBuffer = [];

      const [
        obrasRes,
        tablaEstadosRes,
        contactosRes,
        pedidosRes,
        cotizacionRes,
        visitasRes,
        visitasMetricasRes,
        obraConsultasRes,
        recentVisitasRes,
        recentObraConsultasRes,
      ] = await Promise.all([
        supabase.from("obras").select("id, estado, created_at"),
        supabase
          .from("tabla")
          .select("id, descripcion")
          .eq("cod", 1)
          .gt("orden", 1)
          .order("orden", { ascending: true }),
        supabase.from("contacto").select("id, created_at"),
        supabase.from("pedidos").select("id, created_at, fecha_pedido, obras"),
        supabase.from("cotizacion").select("id, titulo, created_at"),
        supabase.from("visitas").select("id, created_at"),
        supabase.from("visitas").select("session_id, referrer"),
        supabase.from("obra_consultas").select("id, created_at, obra_id, obra_titulo"),
        supabase
          .from("visitas")
          .select("id, pagina, session_id, created_at")
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("obra_consultas")
          .select("id, obra_id, obra_titulo, pagina, created_at")
          .order("created_at", { ascending: false })
          .limit(10),
      ]);

      const obras = obrasRes.data || [];
      const estadosTabla = tablaEstadosRes.data || [];
      const contactos = contactosRes.data || [];
      const pedidos = pedidosRes.data || [];
      const cotizaciones = cotizacionRes.data || [];

      let visitas = [];
      if (visitasRes.error) {
        warningsBuffer.push("No se encontró la tabla de visitas. Se muestran 0 visitas hasta que se active el tracking.");
      } else {
        visitas = visitasRes.data || [];
      }

      let visitasUnicasTotal = 0;
      let visitasPorReferrer = [];
      if (visitasMetricasRes.error) {
        warningsBuffer.push(
          "No se pudieron calcular sesiones únicas o referrers de visitas. Verificá que existan session_id y referrer."
        );
      } else {
        const visitasMetricas = visitasMetricasRes.data || [];

        const uniqueSessions = new Set(
          visitasMetricas
            .map((item) => item.session_id)
            .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
        );
        visitasUnicasTotal = uniqueSessions.size;

        const referrerSessions = visitasMetricas.reduce((acc, item) => {
          const referrerKey = getReferrerLabel(item.referrer);
          const sessionId = item.session_id;

          if (!sessionId || String(sessionId).trim() === "") {
            return acc;
          }

          if (!acc[referrerKey]) {
            acc[referrerKey] = new Set();
          }

          acc[referrerKey].add(String(sessionId));
          return acc;
        }, {});

        visitasPorReferrer = Object.entries(referrerSessions)
          .map(([referrer, sessions]) => ({ referrer, total: sessions.size }))
          .sort((a, b) => b.total - a.total);
      }

      const latestVisits = recentVisitasRes.error ? [] : recentVisitasRes.data || [];

      let obraConsultas = [];
      if (obraConsultasRes.error) {
        obraConsultas = [];
      } else {
        obraConsultas = obraConsultasRes.data || [];
      }

      const latestObraConsultas = recentObraConsultasRes.error ? [] : recentObraConsultasRes.data || [];

      const monthlyVisitas = makeMonthlySeries(visitas, ["created_at"], monthRange);
      const monthlyContactos = makeMonthlySeries(contactos, ["created_at"], monthRange);
      const monthlyPedidos = makeMonthlySeries(pedidos, ["fecha_pedido", "created_at"], monthRange);
      const monthlyObras = makeMonthlySeries(obras, ["created_at"], monthRange);

      // Consultas de obras: primero tabla dedicada, luego fallback a cotizaciones.
      const monthlyConsultas =
        obraConsultas.length > 0
          ? makeMonthlySeries(obraConsultas, ["created_at"], monthRange)
          : makeMonthlySeries(cotizaciones, ["created_at"], monthRange);

      const estadoMap = new Map(estadosTabla.map((item) => [String(item.id), item.descripcion]));
      const statusCounter = {};

      for (const obra of obras) {
        const key = String(obra.estado ?? "sin_estado");
        statusCounter[key] = (statusCounter[key] || 0) + 1;
      }

      const statusEntries = Object.entries(statusCounter)
        .map(([key, value]) => ({
          key,
          name: estadoMap.get(key) || `Estado ${key}`,
          value,
        }))
        .sort((a, b) => b.value - a.value);

      const palette = buildPalette(statusEntries.length);
      const pieData = statusEntries.map((entry, index) => ({
        ...entry,
        color: palette[index],
      }));

      // Ranking de obras consultadas por mes.
      const consultsByMonth = monthRange.map((item) => ({
        monthKey: item.key,
        monthLabel: item.label,
        map: new Map(),
      }));

      const monthIndex = new Map(consultsByMonth.map((m) => [m.monthKey, m]));

      if (obraConsultas.length > 0) {
        for (const row of obraConsultas) {
          const date = getDateValue(row, ["created_at"]);
          const key = monthFromDate(date);
          const monthBucket = monthIndex.get(key);
          if (!monthBucket) continue;

          const name = row.obra_titulo || `Obra #${row.obra_id || "-"}`;
          monthBucket.map.set(name, (monthBucket.map.get(name) || 0) + 1);
        }
      } else {
        // Fallback: uso de cotizaciones por título.
        for (const row of cotizaciones) {
          const date = getDateValue(row, ["created_at"]);
          const key = monthFromDate(date);
          const monthBucket = monthIndex.get(key);
          if (!monthBucket) continue;

          const name = row.titulo || "Sin título";
          monthBucket.map.set(name, (monthBucket.map.get(name) || 0) + 1);
        }
        warningsBuffer.push(
          "No se encontró tabla de consultas de obras. El ranking se calcula con cotizaciones por título."
        );
      }

      const consultsByMonthFormatted = consultsByMonth.map((item) => {
        const items = Array.from(item.map.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 8);

        return {
          monthKey: item.monthKey,
          monthLabel: item.monthLabel,
          items,
        };
      });

      const monthlyBarsData = monthRange.map((item, index) => ({
        key: item.key,
        month: item.label,
        visitas: monthlyVisitas[index]?.value || 0,
        consultas: monthlyConsultas[index]?.value || 0,
        contactos: monthlyContactos[index]?.value || 0,
        pedidos: monthlyPedidos[index]?.value || 0,
        obras: monthlyObras[index]?.value || 0,
      }));

      const consultasTotal = monthlyConsultas.reduce((acc, item) => acc + item.value, 0);

      setKpis({
        visitasTotal: visitas.length,
        visitasUnicasTotal,
        visitasPorReferrer,
        contactosTotal: contactos.length,
        pedidosTotal: pedidos.length,
        obrasTotal: obras.length,
        consultasObraTotal: consultasTotal,
      });

      setMonthlyBars(monthlyBarsData);
      setObraStatusData(pieData);
      setObrasConsultadasPorMes(consultsByMonthFormatted);
      setRecentVisits(latestVisits);
      setRecentObraConsultas(latestObraConsultas);
      setSelectedMonth((prev) => prev || monthRange[monthRange.length - 1]?.key || "");
      setWarnings(warningsBuffer);
    } catch (err) {
      console.error("Error loading dashboard:", err);
      setError("No se pudieron cargar las estadísticas. Revisá la conexión y los permisos de Supabase.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-[#28536b] border-t-transparent" />
          <h2 className="text-xl font-bold text-stone-900">Cargando dashboard</h2>
          <p className="mt-2 text-sm text-stone-500">Estamos preparando las métricas de los últimos meses.</p>
        </div>
      </div>
    );
  }

  const visitsSeries = monthlyBars.map((item) => ({ key: item.key, month: item.month, value: item.visitas }));
  const consultsSeries = monthlyBars.map((item) => ({ key: item.key, month: item.month, value: item.consultas }));
  const contactsSeries = monthlyBars.map((item) => ({ key: item.key, month: item.month, value: item.contactos }));
  const ordersSeries = monthlyBars.map((item) => ({ key: item.key, month: item.month, value: item.pedidos }));
  const obrasSeries = monthlyBars.map((item) => ({ key: item.key, month: item.month, value: item.obras }));

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="overflow-hidden rounded-3xl border border-stone-200 bg-gradient-to-r from-[#f4f0ea] via-white to-[#eef4f8] p-6 md:p-8 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              {/* <p className="mb-3 text-[0.68rem] uppercase tracking-[0.24em] text-[#7a6a58]">Panel de control</p> */}
              <h1 className="text-3xl font-black text-stone-900 md:text-4xl">Panel de estadísticas</h1>
              {/* <p className="mt-2 max-w-2xl text-sm text-stone-600">
                Seguimiento mensual de visitas, consultas de obras, contactos, pedidos y estado actual de obras.
              </p> */}
            </div>

            <button
              type="button"
              onClick={() => loadDashboard({ quiet: true })}
              disabled={refreshing}
              className="inline-flex items-center gap-2 self-start rounded-xl bg-[#28536b] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1f4458] disabled:cursor-not-allowed disabled:opacity-70"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Actualizando..." : "Actualizar"}
            </button>
          </div>
        </header>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {warnings.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-semibold">Avisos de datos</span>
            </div>
            <ul className="space-y-1 text-sm text-amber-700">
              {warnings.map((item) => (
                <li key={item}>• {item}</li>
              ))}
            </ul>
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {/* <KpiCard
            title="Visitas Totales"
            value={kpis.visitasTotal}
            icon={Eye}
            tone="blue"
          /> */}
          <KpiCard
            title="Visitas Únicas"
            value={numberFormatter.format(kpis.visitasUnicasTotal || 0)}
            icon={Eye}
            tone="amber"
          />
          <KpiCard
            title="Visitas Directas"
            value={numberFormatter.format(kpis.visitasPorReferrer[0]?.total || 0)}
            icon={Eye}
            tone="green"
          />
          <KpiCard
            title="Visitas Instagram"
            value={numberFormatter.format(kpis.visitasPorReferrer[1]?.total || 0)}
            icon={Eye}
            tone="pink"
          />
          <KpiCard
            title="Visitas Google"
            value={numberFormatter.format(kpis.visitasPorReferrer[2]?.total || 0)}
            icon={Eye}
            tone="red"
          />
        </section>
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <KpiCard
            title="Consultas de obras"
            value={kpis.consultasObraTotal}
            subtitle="Consultas recibidas en el período"
            icon={ClipboardList}
            tone="amber"
          />
          <KpiCard
            title="Obras"
            value={kpis.obrasTotal}
            subtitle="Total de obras cargadas"
            icon={Package}
            tone="green"
          />
          <KpiCard
            title="Contactos"
            value={kpis.contactosTotal}
            subtitle="Mensajes desde formulario de contacto"
            icon={Mail}
            tone="red"
          />
          <KpiCard
            title="Pedidos"
            value={kpis.pedidosTotal}
            subtitle="Pedidos ingresados"
            icon={Activity}
            tone="blue"
          />
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-stone-900">Últimas 10 visitas</h3>
                <p className="text-xs text-stone-500">Verificación en vivo de ingresos a la app.</p>
              </div>
              <Eye className="h-5 w-5 text-stone-500" />
            </div>

            {recentVisits.length === 0 ? (
              <div className="rounded-xl bg-stone-50 p-4 text-sm text-stone-600">Sin visitas recientes registradas.</div>
            ) : (
              <div className="max-h-72 overflow-auto rounded-xl border border-stone-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-stone-50 text-xs uppercase tracking-[0.08em] text-stone-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Página</th>
                      <th className="px-3 py-2 text-left">Sesión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentVisits.map((item) => (
                      <tr key={item.id} className="border-t border-stone-100 text-stone-700">
                        <td className="px-3 py-2">{item.created_at ? dateTimeFormatter.format(new Date(item.created_at)) : "-"}</td>
                        <td className="px-3 py-2 font-semibold">{item.pagina || "-"}</td>
                        <td className="px-3 py-2 text-xs text-stone-500">{item.session_id ? String(item.session_id).slice(0, 14) : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-stone-900">Últimas 10 consultas de obra</h3>
                <p className="text-xs text-stone-500">Eventos al abrir el detalle de una obra.</p>
              </div>
              <ClipboardList className="h-5 w-5 text-stone-500" />
            </div>

            {recentObraConsultas.length === 0 ? (
              <div className="rounded-xl bg-stone-50 p-4 text-sm text-stone-600">Sin consultas de obra recientes.</div>
            ) : (
              <div className="max-h-72 overflow-auto rounded-xl border border-stone-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-stone-50 text-xs uppercase tracking-[0.08em] text-stone-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Obra</th>
                      <th className="px-3 py-2 text-left">Página</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentObraConsultas.map((item) => (
                      <tr key={item.id} className="border-t border-stone-100 text-stone-700">
                        <td className="px-3 py-2">{item.created_at ? dateTimeFormatter.format(new Date(item.created_at)) : "-"}</td>
                        <td className="px-3 py-2 font-semibold">{item.obra_titulo || `Obra #${item.obra_id || "-"}`}</td>
                        <td className="px-3 py-2">{item.pagina || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <MiniBarChart
            title="Visitas por mes"
            description="Cantidad de ingresos registrados por cada mes"
            series={visitsSeries}
            color="#28536b"
          />
          <MiniBarChart
            title="Consultas de obras por mes"
            description="Solicitudes o consultas agrupadas por mes"
            series={consultsSeries}
            color="#c2945a"
          />
          <MiniBarChart
            title="Contactos por mes"
            description="Mensajes recibidos en la sección de contacto"
            series={contactsSeries}
            color="#9b3d3d"
          />
          <MiniBarChart
            title="Pedidos por mes"
            description="Evolución mensual de pedidos"
            series={ordersSeries}
            color="#2f7d7f"
          />
          <MiniBarChart
            title="Obras ingresadas por mes"
            description="Nuevas obras agregadas a la base"
            series={obrasSeries}
            color="#5b8e7d"
          />

          <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-stone-900">Obras más consultadas por mes</h3>
                <p className="text-xs text-stone-500">
                  Top de obras consultadas para el mes seleccionado.
                </p>
              </div>
              <TrendingUp className="h-5 w-5 text-stone-500" />
            </div>

            <div className="mb-4">
              <label htmlFor="mes-consulta" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">
                Mes
              </label>
              <select
                id="mes-consulta"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#28536b]"
              >
                {monthRange.map((month) => (
                  <option key={month.key} value={month.key}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            {topConsultedThisMonth.length === 0 ? (
              <div className="rounded-xl bg-stone-50 p-4 text-sm text-stone-600">
                No hay consultas registradas en {selectedMonthLabel}.
              </div>
            ) : (
              <div className="space-y-2">
                {topConsultedThisMonth.map((item, index) => (
                  <div
                    key={`${item.name}-${index}`}
                    className="grid grid-cols-[28px,1fr,72px] items-center gap-3 rounded-lg bg-stone-50 px-3 py-2"
                  >
                    <div className="text-sm font-bold text-stone-500">{index + 1}</div>
                    <div className="truncate text-sm text-stone-800">{item.name}</div>
                    <div className="text-right text-sm font-bold text-stone-900">{item.count}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DonutChart
            title="Distribución de obras por estado"
            subtitle="Cantidad actual de obras según estado"
            data={obraStatusData}
          />

          <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-stone-900">Resumen mensual consolidado</h3>
                <p className="text-xs text-stone-500">Comparación rápida de cada indicador por mes.</p>
              </div>
              <BarChart3 className="h-5 w-5 text-stone-500" />
            </div>

            <div className="max-h-[360px] overflow-auto rounded-xl border border-stone-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-stone-50 text-xs uppercase tracking-[0.08em] text-stone-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Mes</th>
                    <th className="px-3 py-2 text-right">Visitas</th>
                    <th className="px-3 py-2 text-right">Consultas</th>
                    <th className="px-3 py-2 text-right">Contactos</th>
                    <th className="px-3 py-2 text-right">Pedidos</th>
                    <th className="px-3 py-2 text-right">Obras</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyBars.map((row) => (
                    <tr key={row.key} className="border-t border-stone-100 text-stone-700">
                      <td className="px-3 py-2 font-semibold">{row.month}</td>
                      <td className="px-3 py-2 text-right">{numberFormatter.format(row.visitas)}</td>
                      <td className="px-3 py-2 text-right">{numberFormatter.format(row.consultas)}</td>
                      <td className="px-3 py-2 text-right">{numberFormatter.format(row.contactos)}</td>
                      <td className="px-3 py-2 text-right">{numberFormatter.format(row.pedidos)}</td>
                      <td className="px-3 py-2 text-right">{numberFormatter.format(row.obras)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}